/**
 * Performance Monitoring Service
 *
 * Tracks Core Web Vitals, route changes, API calls, and component render times.
 * Reports metrics to backend for analysis and alerts on budget violations.
 */

import { logger } from '@/utils/logger';

const SHOULD_LOG_BUDGETS =
  import.meta.env.DEV || import.meta.env.VITE_PERF_LOGS === 'true';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface WebVitalsMetric {
  name: 'LCP' | 'FID' | 'CLS' | 'TTFB' | 'FCP' | 'INP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  entries: PerformanceEntry[];
}

export interface RouteChangeMetric {
  from: string;
  to: string;
  duration: number;
  timestamp: number;
  navigationId: string;
}

export interface APICallMetric {
  url: string;
  method: string;
  duration: number;
  status: number;
  timestamp: number;
  requestId: string;
  size?: number;
  cached?: boolean;
}

export interface ComponentRenderMetric {
  componentName: string;
  renderTime: number;
  updateCount: number;
  timestamp: number;
  props?: Record<string, unknown>;
}

export interface PerformanceBudget {
  LCP: number;  // Largest Contentful Paint (ms)
  FID: number;  // First Input Delay (ms)
  CLS: number;  // Cumulative Layout Shift (score)
  TTFB: number; // Time to First Byte (ms)
  FCP: number;  // First Contentful Paint (ms)
  INP: number;  // Interaction to Next Paint (ms)
  routeChange: number; // Route change duration (ms)
  apiCall: number;     // API call duration (ms)
  componentRender: number; // Component render time (ms)
}

export interface PerformanceReport {
  sessionId: string;
  userId?: string;
  timestamp: number;
  url: string;
  userAgent: string;
  webVitals: WebVitalsMetric[];
  routeChanges: RouteChangeMetric[];
  apiCalls: APICallMetric[];
  componentRenders: ComponentRenderMetric[];
  budgetViolations: BudgetViolation[];
  deviceInfo: DeviceInfo;
}

export interface BudgetViolation {
  metric: string;
  budget: number;
  actual: number;
  timestamp: number;
  context?: string;
}

export interface DeviceInfo {
  deviceMemory?: number;
  hardwareConcurrency?: number;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

// =============================================================================
// PERFORMANCE BUDGETS (Recommended thresholds)
// =============================================================================

export const DEFAULT_PERFORMANCE_BUDGETS: PerformanceBudget = {
  LCP: 2500,      // Good: < 2.5s
  FID: 100,       // Good: < 100ms
  CLS: 0.1,       // Good: < 0.1
  TTFB: 800,      // Good: < 800ms
  FCP: 1800,      // Good: < 1.8s
  INP: 200,       // Good: < 200ms
  routeChange: 1000, // Route changes should be under 1s
  apiCall: 3000,     // API calls should be under 3s
  componentRender: 16, // Target 60fps (16ms per frame)
};

// Web Vitals thresholds for rating
const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  TTFB: { good: 800, poor: 1800 },
  FCP: { good: 1800, poor: 3000 },
  INP: { good: 200, poor: 500 },
};

// =============================================================================
// PERFORMANCE MONITORING CLASS
// =============================================================================

class PerformanceMonitoringService {
  private sessionId: string;
  private userId?: string;
  private webVitals: WebVitalsMetric[] = [];
  private routeChanges: RouteChangeMetric[] = [];
  private apiCalls: APICallMetric[] = [];
  private componentRenders: ComponentRenderMetric[] = [];
  private budgetViolations: BudgetViolation[] = [];
  private budgets: PerformanceBudget;
  private isInitialized = false;
  private reportingEndpoint: string;
  private reportingInterval: number;
  private reportingTimer?: ReturnType<typeof setInterval>;
  private observers: Map<string, PerformanceObserver> = new Map();
  private currentRoute: string = '';
  private routeChangeStartTime: number = 0;
  private navigationId = 0;
  private onBudgetViolation?: (violation: BudgetViolation) => void;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.budgets = { ...DEFAULT_PERFORMANCE_BUDGETS };
    this.reportingEndpoint = '/api/v1/performance/metrics';
    this.reportingInterval = 30000; // Report every 30 seconds
  }

  // ---------------------------------------------------------------------------
  // INITIALIZATION
  // ---------------------------------------------------------------------------

  /**
   * Initialize performance monitoring
   */
  init(options?: {
    userId?: string;
    budgets?: Partial<PerformanceBudget>;
    reportingEndpoint?: string;
    reportingInterval?: number;
    onBudgetViolation?: (violation: BudgetViolation) => void;
  }): void {
    if (this.isInitialized) {
      logger.warn('[Performance] Already initialized');
      return;
    }

    if (typeof window === 'undefined') {
      logger.warn('[Performance] Not running in browser environment');
      return;
    }

    this.userId = options?.userId;
    this.budgets = { ...this.budgets, ...options?.budgets };
    this.reportingEndpoint = options?.reportingEndpoint || this.reportingEndpoint;
    this.reportingInterval = options?.reportingInterval || this.reportingInterval;
    this.onBudgetViolation = options?.onBudgetViolation;
    this.currentRoute = window.location.pathname;

    this.initWebVitalsObservers();
    this.initLongTaskObserver();
    this.initResourceObserver();
    this.startReportingTimer();

    this.isInitialized = true;
    logger.info('[Performance] Monitoring initialized', { sessionId: this.sessionId });
  }

  /**
   * Cleanup and stop monitoring
   */
  destroy(): void {
    this.stopReportingTimer();
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
    this.isInitialized = false;
    logger.info('[Performance] Monitoring destroyed');
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Update performance budgets
   */
  setBudgets(budgets: Partial<PerformanceBudget>): void {
    this.budgets = { ...this.budgets, ...budgets };
  }

  // ---------------------------------------------------------------------------
  // CORE WEB VITALS
  // ---------------------------------------------------------------------------

  private initWebVitalsObservers(): void {
    // LCP - Largest Contentful Paint
    this.observeLCP();

    // FID - First Input Delay (deprecated, but still tracked)
    this.observeFID();

    // CLS - Cumulative Layout Shift
    this.observeCLS();

    // TTFB - Time to First Byte
    this.measureTTFB();

    // FCP - First Contentful Paint
    this.observeFCP();

    // INP - Interaction to Next Paint
    this.observeINP();
  }

  private observeLCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };

        if (lastEntry) {
          this.recordWebVital({
            name: 'LCP',
            value: lastEntry.startTime,
            entries: [lastEntry],
          });
        }
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.set('lcp', observer);
    } catch (e) {
      logger.warn('[Performance] LCP observer not supported');
    }
  }

  private observeFID(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstEntry = entries[0] as PerformanceEntry & { processingStart: number; startTime: number };

        if (firstEntry) {
          this.recordWebVital({
            name: 'FID',
            value: firstEntry.processingStart - firstEntry.startTime,
            entries: [firstEntry],
          });
        }
      });

      observer.observe({ type: 'first-input', buffered: true });
      this.observers.set('fid', observer);
    } catch (e) {
      logger.warn('[Performance] FID observer not supported');
    }
  }

  private observeCLS(): void {
    try {
      let clsValue = 0;
      let clsEntries: PerformanceEntry[] = [];
      let sessionValue = 0;
      let sessionEntries: PerformanceEntry[] = [];

      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const layoutShiftEntry = entry as PerformanceEntry & {
            hadRecentInput: boolean;
            value: number;
          };

          // Only count layout shifts without recent input
          if (!layoutShiftEntry.hadRecentInput) {
            const firstSessionEntry = sessionEntries[0] as PerformanceEntry | undefined;
            const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as PerformanceEntry | undefined;

            // If entry is within 1 second and 5 seconds of session window
            if (
              sessionValue &&
              firstSessionEntry &&
              lastSessionEntry &&
              entry.startTime - lastSessionEntry.startTime < 1000 &&
              entry.startTime - firstSessionEntry.startTime < 5000
            ) {
              sessionValue += layoutShiftEntry.value;
              sessionEntries.push(entry);
            } else {
              sessionValue = layoutShiftEntry.value;
              sessionEntries = [entry];
            }

            // Update CLS if current session is larger
            if (sessionValue > clsValue) {
              clsValue = sessionValue;
              clsEntries = [...sessionEntries];

              this.recordWebVital({
                name: 'CLS',
                value: clsValue,
                entries: clsEntries,
              });
            }
          }
        }
      });

      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.set('cls', observer);
    } catch (e) {
      logger.warn('[Performance] CLS observer not supported');
    }
  }

  private measureTTFB(): void {
    try {
      const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navEntry) {
        const ttfb = navEntry.responseStart - navEntry.requestStart;
        this.recordWebVital({
          name: 'TTFB',
          value: ttfb,
          entries: [navEntry],
        });
      }
    } catch (e) {
      logger.warn('[Performance] TTFB measurement not supported');
    }
  }

  private observeFCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcpEntry = entries.find((entry) => entry.name === 'first-contentful-paint');

        if (fcpEntry) {
          this.recordWebVital({
            name: 'FCP',
            value: fcpEntry.startTime,
            entries: [fcpEntry],
          });
        }
      });

      observer.observe({ type: 'paint', buffered: true });
      this.observers.set('fcp', observer);
    } catch (e) {
      logger.warn('[Performance] FCP observer not supported');
    }
  }

  private observeINP(): void {
    try {
      let maxINP = 0;
      const inpEntries: PerformanceEntry[] = [];

      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const eventEntry = entry as PerformanceEntry & {
            processingStart: number;
            processingEnd: number;
            duration: number;
          };

          // INP considers the worst interaction
          if (eventEntry.duration > maxINP) {
            maxINP = eventEntry.duration;
            inpEntries.push(entry);

            this.recordWebVital({
              name: 'INP',
              value: maxINP,
              entries: inpEntries.slice(-10), // Keep last 10 entries
            });
          }
        }
      });

      observer.observe({ type: 'event', buffered: true, durationThreshold: 16 });
      this.observers.set('inp', observer);
    } catch (e) {
      logger.warn('[Performance] INP observer not supported');
    }
  }

  private recordWebVital(metric: {
    name: WebVitalsMetric['name'];
    value: number;
    entries: PerformanceEntry[];
  }): void {
    const thresholds = WEB_VITALS_THRESHOLDS[metric.name];
    let rating: WebVitalsMetric['rating'] = 'good';

    if (metric.value > thresholds.poor) {
      rating = 'poor';
    } else if (metric.value > thresholds.good) {
      rating = 'needs-improvement';
    }

    // Find existing metric to calculate delta
    const existingMetric = this.webVitals.find((m) => m.name === metric.name);
    const delta = existingMetric ? metric.value - existingMetric.value : metric.value;

    const webVitalMetric: WebVitalsMetric = {
      name: metric.name,
      value: metric.value,
      rating,
      delta,
      id: this.generateId(),
      navigationType: this.getNavigationType(),
      entries: metric.entries,
    };

    // Update or add metric
    const existingIndex = this.webVitals.findIndex((m) => m.name === metric.name);
    if (existingIndex >= 0) {
      this.webVitals[existingIndex] = webVitalMetric;
    } else {
      this.webVitals.push(webVitalMetric);
    }

    // Check budget
    const budget = this.budgets[metric.name];
    if (metric.value > budget) {
      this.recordBudgetViolation({
        metric: metric.name,
        budget,
        actual: metric.value,
        timestamp: Date.now(),
        context: `Web Vital ${metric.name} exceeded budget`,
      });
    }

    logger.debug(`[Performance] ${metric.name}: ${metric.value.toFixed(2)} (${rating})`);
  }

  // ---------------------------------------------------------------------------
  // LONG TASK OBSERVER
  // ---------------------------------------------------------------------------

  private initLongTaskObserver(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            logger.debug('[Performance] Long task detected', {
              duration: entry.duration,
              startTime: entry.startTime,
            });
          }
        }
      });

      observer.observe({ type: 'longtask', buffered: true });
      this.observers.set('longtask', observer);
    } catch (e) {
      logger.warn('[Performance] Long task observer not supported');
    }
  }

  // ---------------------------------------------------------------------------
  // RESOURCE OBSERVER
  // ---------------------------------------------------------------------------

  private initResourceObserver(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const resourceEntry = entry as PerformanceResourceTiming;

          // Track slow resources
          if (resourceEntry.duration > 1000) {
            logger.debug('[Performance] Slow resource', {
              name: resourceEntry.name,
              duration: resourceEntry.duration,
              transferSize: resourceEntry.transferSize,
            });
          }
        }
      });

      observer.observe({ type: 'resource', buffered: true });
      this.observers.set('resource', observer);
    } catch (e) {
      logger.warn('[Performance] Resource observer not supported');
    }
  }

  // ---------------------------------------------------------------------------
  // ROUTE CHANGE TRACKING
  // ---------------------------------------------------------------------------

  /**
   * Start tracking a route change
   */
  startRouteChange(from: string, to: string): string {
    this.routeChangeStartTime = performance.now();
    this.navigationId++;

    const navigationId = `nav-${this.navigationId}-${Date.now()}`;

    logger.debug('[Performance] Route change started', { from, to, navigationId });

    return navigationId;
  }

  /**
   * End tracking a route change
   */
  endRouteChange(navigationId: string, to: string): RouteChangeMetric {
    const duration = performance.now() - this.routeChangeStartTime;
    const from = this.currentRoute;

    const metric: RouteChangeMetric = {
      from,
      to,
      duration,
      timestamp: Date.now(),
      navigationId,
    };

    this.routeChanges.push(metric);
    this.currentRoute = to;

    // Check budget
    if (duration > this.budgets.routeChange) {
      this.recordBudgetViolation({
        metric: 'routeChange',
        budget: this.budgets.routeChange,
        actual: duration,
        timestamp: Date.now(),
        context: `Route change from ${from} to ${to}`,
      });
    }

    // Keep only last 100 route changes
    if (this.routeChanges.length > 100) {
      this.routeChanges = this.routeChanges.slice(-100);
    }

    logger.debug('[Performance] Route change completed', { duration: duration.toFixed(2), from, to });

    return metric;
  }

  // ---------------------------------------------------------------------------
  // API CALL TRACKING
  // ---------------------------------------------------------------------------

  /**
   * Track an API call's performance
   */
  trackAPICall(metric: Omit<APICallMetric, 'timestamp'>): void {
    const fullMetric: APICallMetric = {
      ...metric,
      timestamp: Date.now(),
    };

    this.apiCalls.push(fullMetric);

    // Check budget
    if (metric.duration > this.budgets.apiCall) {
      this.recordBudgetViolation({
        metric: 'apiCall',
        budget: this.budgets.apiCall,
        actual: metric.duration,
        timestamp: Date.now(),
        context: `${metric.method} ${metric.url}`,
      });
    }

    // Keep only last 500 API calls
    if (this.apiCalls.length > 500) {
      this.apiCalls = this.apiCalls.slice(-500);
    }

    logger.debug('[Performance] API call tracked', {
      url: metric.url,
      duration: metric.duration.toFixed(2),
      status: metric.status,
    });
  }

  // ---------------------------------------------------------------------------
  // COMPONENT RENDER TRACKING
  // ---------------------------------------------------------------------------

  /**
   * Track a component's render time
   */
  trackComponentRender(metric: Omit<ComponentRenderMetric, 'timestamp'>): void {
    const fullMetric: ComponentRenderMetric = {
      ...metric,
      timestamp: Date.now(),
    };

    this.componentRenders.push(fullMetric);

    // Check budget (only warn for renders over 16ms for 60fps)
    if (metric.renderTime > this.budgets.componentRender) {
      this.recordBudgetViolation({
        metric: 'componentRender',
        budget: this.budgets.componentRender,
        actual: metric.renderTime,
        timestamp: Date.now(),
        context: metric.componentName,
      });
    }

    // Keep only last 500 component renders
    if (this.componentRenders.length > 500) {
      this.componentRenders = this.componentRenders.slice(-500);
    }
  }

  // ---------------------------------------------------------------------------
  // BUDGET VIOLATIONS
  // ---------------------------------------------------------------------------

  private recordBudgetViolation(violation: BudgetViolation): void {
    this.budgetViolations.push(violation);

    // Keep only last 100 violations
    if (this.budgetViolations.length > 100) {
      this.budgetViolations = this.budgetViolations.slice(-100);
    }

    // Callback for custom handling
    if (this.onBudgetViolation) {
      this.onBudgetViolation(violation);
    }

    // Log warning (dev-only unless explicitly enabled)
    if (SHOULD_LOG_BUDGETS) {
      logger.warn('[Performance] Budget violation', {
        metric: violation.metric,
        budget: violation.budget,
        actual: violation.actual.toFixed(2),
        context: violation.context,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // REPORTING
  // ---------------------------------------------------------------------------

  private startReportingTimer(): void {
    this.reportingTimer = setInterval(() => {
      this.sendReport();
    }, this.reportingInterval);

    // Also send report on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.sendReport(true);
        }
      });

      window.addEventListener('pagehide', () => {
        this.sendReport(true);
      });
    }
  }

  private stopReportingTimer(): void {
    if (this.reportingTimer) {
      clearInterval(this.reportingTimer);
      this.reportingTimer = undefined;
    }
  }

  /**
   * Generate and send performance report
   */
  async sendReport(useBeacon = false): Promise<void> {
    if (!this.isInitialized) return;

    const report = this.generateReport();

    // Don't send empty reports
    if (
      report.webVitals.length === 0 &&
      report.routeChanges.length === 0 &&
      report.apiCalls.length === 0 &&
      report.componentRenders.length === 0
    ) {
      return;
    }

    try {
      if (useBeacon && navigator.sendBeacon) {
        // Use sendBeacon for page unload
        navigator.sendBeacon(
          this.reportingEndpoint,
          JSON.stringify(report)
        );
        logger.debug('[Performance] Report sent via beacon');
      } else {
        // Use fetch for regular reporting
        await fetch(this.reportingEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(report),
          keepalive: true,
        });
        logger.debug('[Performance] Report sent via fetch');
      }

      // Clear reported data
      this.clearReportedData();
    } catch (error) {
      logger.error('[Performance] Failed to send report', error);
    }
  }

  /**
   * Generate performance report
   */
  generateReport(): PerformanceReport {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: Date.now(),
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      webVitals: [...this.webVitals],
      routeChanges: [...this.routeChanges],
      apiCalls: [...this.apiCalls],
      componentRenders: [...this.componentRenders],
      budgetViolations: [...this.budgetViolations],
      deviceInfo: this.getDeviceInfo(),
    };
  }

  private clearReportedData(): void {
    // Keep web vitals (they're cumulative)
    // Clear other metrics after reporting
    this.routeChanges = [];
    this.apiCalls = [];
    this.componentRenders = [];
    this.budgetViolations = [];
  }

  // ---------------------------------------------------------------------------
  // GETTERS
  // ---------------------------------------------------------------------------

  /**
   * Get current web vitals
   */
  getWebVitals(): WebVitalsMetric[] {
    return [...this.webVitals];
  }

  /**
   * Get route change metrics
   */
  getRouteChanges(): RouteChangeMetric[] {
    return [...this.routeChanges];
  }

  /**
   * Get API call metrics
   */
  getAPICallMetrics(): APICallMetric[] {
    return [...this.apiCalls];
  }

  /**
   * Get component render metrics
   */
  getComponentRenderMetrics(): ComponentRenderMetric[] {
    return [...this.componentRenders];
  }

  /**
   * Get budget violations
   */
  getBudgetViolations(): BudgetViolation[] {
    return [...this.budgetViolations];
  }

  /**
   * Get current budgets
   */
  getBudgets(): PerformanceBudget {
    return { ...this.budgets };
  }

  // ---------------------------------------------------------------------------
  // UTILITY METHODS
  // ---------------------------------------------------------------------------

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getNavigationType(): string {
    if (typeof window === 'undefined' || !performance.getEntriesByType) {
      return 'unknown';
    }

    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    return navEntry?.type || 'unknown';
  }

  private getDeviceInfo(): DeviceInfo {
    const info: DeviceInfo = {};

    if (typeof navigator === 'undefined') {
      return info;
    }

    // Device memory (in GB)
    if ('deviceMemory' in navigator) {
      info.deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    }

    // CPU cores
    if ('hardwareConcurrency' in navigator) {
      info.hardwareConcurrency = navigator.hardwareConcurrency;
    }

    // Network information
    const connection = (navigator as Navigator & {
      connection?: {
        type?: string;
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
    }).connection;

    if (connection) {
      info.connectionType = connection.type;
      info.effectiveType = connection.effectiveType;
      info.downlink = connection.downlink;
      info.rtt = connection.rtt;
      info.saveData = connection.saveData;
    }

    return info;
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const performanceMonitor = new PerformanceMonitoringService();

// =============================================================================
// API INTERCEPTOR INTEGRATION
// =============================================================================

/**
 * Create axios request interceptor for API call tracking
 */
export function createAPIPerformanceInterceptor(axiosInstance: {
  interceptors: {
    request: {
      use: (
        onFulfilled?: (config: { metadata?: { startTime: number; requestId: string }; url?: string; method?: string }) => { metadata?: { startTime: number; requestId: string }; url?: string; method?: string },
        onRejected?: (error: unknown) => Promise<never>
      ) => number;
    };
    response: {
      use: (
        onFulfilled?: (response: { config: { metadata?: { startTime: number; requestId: string }; url?: string; method?: string }; status: number; headers?: { 'content-length'?: string }; request?: { fromCache?: boolean } }) => { config: { metadata?: { startTime: number; requestId: string }; url?: string; method?: string }; status: number; headers?: { 'content-length'?: string }; request?: { fromCache?: boolean } },
        onRejected?: (error: { config?: { metadata?: { startTime: number; requestId: string }; url?: string; method?: string }; response?: { status: number } }) => Promise<never>
      ) => number;
    };
  };
}): void {
  // Request interceptor - add start time
  axiosInstance.interceptors.request.use((config) => {
    config.metadata = {
      startTime: performance.now(),
      requestId: `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    return config;
  });

  // Response interceptor - calculate duration and track
  axiosInstance.interceptors.response.use(
    (response) => {
      const metadata = response.config.metadata;
      if (metadata) {
        const duration = performance.now() - metadata.startTime;
        performanceMonitor.trackAPICall({
          url: response.config.url || '',
          method: (response.config.method || 'GET').toUpperCase(),
          duration,
          status: response.status,
          requestId: metadata.requestId,
          size: parseInt(response.headers?.['content-length'] || '0', 10),
          cached: response.request?.fromCache || false,
        });
      }
      return response;
    },
    (error) => {
      const metadata = error.config?.metadata;
      if (metadata) {
        const duration = performance.now() - metadata.startTime;
        performanceMonitor.trackAPICall({
          url: error.config?.url || '',
          method: (error.config?.method || 'GET').toUpperCase(),
          duration,
          status: error.response?.status || 0,
          requestId: metadata.requestId,
        });
      }
      return Promise.reject(error);
    }
  );
}

export default performanceMonitor;
